import { useQuery } from "react-query";
import { getClientInsurancesAsync } from "api/insurance";
import { ClientInsurance } from "api/types/insurance";
import { GET_CLIENT_INSURANCES, STALE_TIME_5_MINUTES } from "@constants/reactQueryKeys";

/**
 * Returns true when the client has ≥1 active, unexpired insurance (primary or secondary)
 * that is flagged as Medicaid and has coverage_state === "VA".
 *
 * Used to gate the "Assessment - In Person" service type warning in the scheduling flow.
 * In-person assessments are contractually reserved for VA Medicaid clients.
 *
 * @param insurances - The array of insurance records for a client. Each record is
 *   expected to expose `active`, `expiration_date`, `is_medicaid`, and `coverage_state` fields. The helper is
 *   defensive against missing/undefined data and returns `false` in those cases.
 */
const getLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export function isClientVAMedicaid(insurances: ClientInsurance[] | null | undefined): boolean {
  if (!Array.isArray(insurances) || insurances.length === 0) return false;
  const today = getLocalDateString(new Date());
  return insurances.some(
    (ins) =>
      Boolean(ins?.active) &&
      Boolean(ins?.is_medicaid) &&
      ins?.coverage_state === "VA" &&
      (ins.expiration_date == null || ins.expiration_date.slice(0, 10) > today)
  );
}

/**
 * Returns `{ isVAMedicaid, isLoading }` for the given client. The check inspects all
 * active insurances (primary or secondary) and returns true when at least one is
 * flagged as VA Medicaid (`is_medicaid = true` AND `coverage_state = "VA"`) and
 * is not expired.
 *
 * Used by the scheduling flow to decide whether to surface the "not VA Medicaid"
 * warning when a user picks the "Assessment - In Person" service type.
 */
export default function useIsClientVAMedicaid(clientId: string | number | null | undefined) {
  const { data, isLoading } = useQuery({
    queryKey: [GET_CLIENT_INSURANCES, clientId],
    queryFn: () => getClientInsurancesAsync(clientId!),
    enabled: !!clientId,
    staleTime: STALE_TIME_5_MINUTES,
  });

  const insurances: ClientInsurance[] = data?.data ?? [];
  return {
    isVAMedicaid: isClientVAMedicaid(insurances),
    isLoading,
  };
}
